import React from "react";
import { Box, Group, Stack } from "@mantine/core";
import { useAuth } from "hooks/useAuth";
import { useInitialModals } from "hooks/useInitialModals";
import { ChartPanel } from "./UI/ChartPanel/ChartPanel";
import { AuthenticatedUserMenu } from "./UI/LeftMenu/AuthenticatedUserMenu";
import { UnauthenticatedUserMenu } from "./UI/LeftMenu/Unauthenticated/UnauthenticatedUserMenu";
import { LegendPanel } from "./UI/LegendPanel/LegendPanel";
import { TopEditMenu } from "./UI/TopEditMenu/TopEditMenu";
import { LeftMenuWrapper } from "./UI/Wrappers/LeftMenuWrapper";
import { MapWrapper } from "./UI/Wrappers/MapWrapper";
import { useIsSmallViewport } from "hooks/useIsSmallViewport";
import { useTranslation } from "react-i18next";
import { useMobileMenu } from "hooks/useMobileMenu";
import { PANEL_TOGGLE_ANIMATION_DURATION } from "config/constants";
import "./MainContainer.css";
import classes from "./MainContainer.module.css";

function NavbarItem({
  text,
  link,
  active = false,
}: {
  text: string;
  link: string;
  active?: boolean;
}) {
  return (
    <li className="item" role="none">
      <a
        className={classes.navbarLink}
        data-active={active || undefined}
        role="menuitem"
        href={link}
      >
        {text}
      </a>
    </li>
  );
}

function Navbar() {
  const isSmallViewport = useIsSmallViewport();
  const isRunningInsideElectron =
    /electron/i.test(navigator.userAgent) || globalThis.isElectron;
  return !isSmallViewport && !isRunningInsideElectron ? (
    <nav className={classes.navbar}>
      <ul className="header-menu" role="menubar">
        <NavbarItem link="https://vecturagames.com" text="Home" />
        <NavbarItem link="https://blog.vecturagames.com" text="Blog" />
        <NavbarItem link="https://gpxviewer.com" text="GPX Viewer" />
        <NavbarItem link="https://trackbook.com" text="Trackbook" active />
      </ul>
    </nav>
  ) : null;
}

function MobileMenuItem({
  text,
  link,
  active = false,
}: {
  text: string;
  link: string;
  active?: boolean;
}) {
  return (
    <li role="none">
      <a
        className={classes.mobileMenuLink}
        data-active={active || undefined}
        role="menuitem"
        href={link}
      >
        {text}
      </a>
    </li>
  );
}

function MobileMenu() {
  const { t } = useTranslation();
  const { isMobileMenuOpen, toggleMobileMenu } = useMobileMenu();

  return (
    <nav
      className={`mobile-menu ${classes.mobileMenu}`}
      style={{
        height: 190,
        marginTop: isMobileMenuOpen ? 0 : -190,
        transition: `margin-top ${PANEL_TOGGLE_ANIMATION_DURATION}s`,
      }}
    >
      <ul role="menubar">
        <MobileMenuItem link="https://vecturagames.com" text="Home" />
        <MobileMenuItem link="https://blog.vecturagames.com" text="Blog" />
        <MobileMenuItem
          link="https://vecturagames.com/gpxviewer/"
          text="GPX Viewer"
        />
        <MobileMenuItem link="#" text="Trackbook" active />
      </ul>
      <a
        className="x-icon-btn"
        type="button"
        aria-expanded={isMobileMenuOpen}
        aria-label={t("mobile_menu.close_label")}
        onClick={() => toggleMobileMenu()}
      >
        <i
          className={`fa fa-x ${classes.mobileMenuClose}`}
          title={t("mobile_menu.close_label")}
        ></i>
      </a>
    </nav>
  );
}

export const MainContainer = () => {
  const { user } = useAuth();

  useInitialModals(user);

  return (
    <Box style={{ width: "100%", height: "100%", position: "absolute" }}>
      <Navbar />
      <Group
        style={{
          height: "100%",
          width: "100%",
          flexWrap: "nowrap",
          overflow: "hidden",
          // The legend panel is positioned in it, so it's clipped while it slides out
          position: "relative",
        }}
        gap={0}
      >
        <MobileMenu />
        <LeftMenuWrapper>
          <div></div>
          {user ? <AuthenticatedUserMenu /> : <UnauthenticatedUserMenu />}
        </LeftMenuWrapper>
        <Stack
          id="mapchartwrapper"
          style={{
            height: "100%",
            flexGrow: 1,
            minWidth: 0,
            overflow: "hidden",
            position: "relative",
          }}
          gap={0}
        >
          <MapWrapper />
          <ChartPanel />
          <TopEditMenu />
        </Stack>
        <LegendPanel />
        {/* {isDev() && <DebugPanel />} */}
      </Group>
    </Box>
  );
};
